/**
 * OUW-260: la grilla de horarios que el cliente puede proponer.
 *
 * Son las MISMAS reglas que valida api-client, escritas acá para que el modal
 * no ofrezca lo que el servidor va a rechazar. El servidor sigue siendo la
 * autoridad: esto evita el viaje, no lo reemplaza.
 *
 * Excepción (OUW-336): las reglas del examen médico (4 días hábiles, 9 a 16) y
 * la franja horaria son más estrictas que lo que valida el servidor (12 h y
 * :00/:30) y viven SOLO acá. Lo propuesto lo confirma un analista.
 */

import { addCalendarDays, addMxBusinessDays, isWeekendDay } from './mxHolidays';

/**
 * OUW-336: el modal sirve a dos requisitos con reglas distintas. La videollamada
 * (`interview`) es la de OUW-260; el examen médico con Olé (`ole_medical_exam`,
 * médico + laboratorio) necesita margen para coordinar y solo el horario de
 * laboratorio. Los códigos son los de api-client (`requirement.go`).
 */
export type AppointmentType = 'interview' | 'medical_exam';

const OLE_MEDICAL_EXAM_CODE = 'ole_medical_exam';

/**
 * OUW-364: las únicas citas que agenda Olé (`RequirementNeedsSlot` en
 * api-client). El chequeo con el médico propio (`client_physician_checkup`) lo
 * coordina el cliente: aunque una copia vieja del catálogo traiga `schedule`,
 * no hay horario que proponerle a nadie.
 */
const OLE_BOOKED_CODES = new Set(['interview', OLE_MEDICAL_EXAM_CODE]);

export function isOleBookedAppointment(requirement: { code?: string }): boolean {
  return OLE_BOOKED_CODES.has((requirement.code ?? '').trim());
}

/** Cualquier código que no sea el del examen es la videollamada: es el único otro que agenda. */
export function appointmentTypeFor(requirement: { code?: string }): AppointmentType {
  return (requirement.code ?? '').trim() === OLE_MEDICAL_EXAM_CODE ? 'medical_exam' : 'interview';
}

/**
 * Examen médico (Lucas, 07-oct): la primera fecha es a 4 días hábiles de hoy
 * (sin fines de semana ni feriados oficiales de México), contados en hora de
 * Ciudad de México.
 *
 * En las dos citas que agenda Olé (videollamada y examen) el calendario solo
 * deja lunes a viernes. Un feriado que cae entre semana sigue disponible: se
 * cierra el fin de semana, no el feriado. La franja horaria no cambia. La
 * regla vive en este modal; el resto de los date pickers de la app no la usa.
 */
const EXAM_MIN_BUSINESS_DAYS = 4;

/**
 * Examen médico: horario normal de laboratorio, 9 a 17. Cada opción es el
 * comienzo de una cita de una hora, así que la última es a las 16:00. La
 * recomendación de ir de mañana (ayuno) va en el copy, no como restricción.
 */
const EXAM_FIRST_HOUR = 9;
const EXAM_LAST_HOUR = 16;

/** Hasta tres opciones. Una sola no le deja margen al analista. */
export const MAX_AVAILABILITY_OPTIONS = 3;

/** La videollamada dura una hora: el cliente elige el comienzo, no el largo. */
export const MEETING_DURATION_MINUTES = 60;

/**
 * Tope de la nota, el mismo que valida el servidor (500). Sin él, el servidor
 * rechazaba una nota larga con 400 y el modal lo mostraba como «alguno de los
 * horarios no sirve», que no tiene nada que ver con lo que pasó.
 */
export const MAX_NOTE_LENGTH = 500;

/**
 * Margen mínimo entre proponer y la primera opción: el servidor pide 12 horas
 * (`requirementAvailabilityMinLead`). Se replica acá para no ofrecer lo que va
 * a rechazar.
 *
 * NO alcanza con empezar «mañana»: a las 22:00 el selector ofrecía las 09:00
 * del día siguiente —11 horas— y el servidor respondía 400 con un mensaje que
 * hablaba de otra cosa. El filtro se aplica por día: en el primero se ocultan
 * las horas que caen dentro del margen.
 */
const MIN_LEAD_MS = 12 * 60 * 60 * 1000;
const MIN_LEAD_DAYS = 1;

/**
 * Techo de la ventana: proponer para dentro de tres meses no le sirve a nadie
 * y deja la solicitud parada. No es una regla del servidor — es lo que el
 * selector ofrece.
 */
const MAX_LEAD_DAYS = 30;

/** Franja horaria laboral, en pasos de media hora, como valida el servidor. */
const FIRST_HOUR = 9;
const LAST_HOUR = 18;

/**
 * Horas elegibles: `09:00`, `09:30`, … `18:00`. El servidor exige en punto o
 * y media; ofrecer otra cosa sería ofrecer un 400.
 */
export function availabilitySlotHours(type: AppointmentType = 'interview'): string[] {
  const first = type === 'medical_exam' ? EXAM_FIRST_HOUR : FIRST_HOUR;
  const last = type === 'medical_exam' ? EXAM_LAST_HOUR : LAST_HOUR;
  const out: string[] = [];
  for (let hour = first; hour <= last; hour += 1) {
    out.push(`${`${hour}`.padStart(2, '0')}:00`);
    if (hour !== last) out.push(`${`${hour}`.padStart(2, '0')}:30`);
  }
  return out;
}

/**
 * Las horas elegibles PARA UN DÍA: la grilla completa, menos las que caen
 * dentro del margen de 12 horas que exige el servidor. Sin día elegido se
 * devuelve la grilla entera, para que el selector no quede vacío.
 */
export function availabilitySlotHoursFor(
  day: string,
  now: Date = new Date(),
  type: AppointmentType = 'interview',
): string[] {
  const all = availabilitySlotHours(type);
  if (!day) return all;
  // Sábado y domingo no se agendan, ni en la videollamada ni en el examen.
  if (isWeekendDay(day)) return [];
  // Examen: un día fuera de la ventana (tipeado a mano, o una propuesta vieja)
  // no ofrece horas, así la fila no se puede completar.
  if (
    type === 'medical_exam' &&
    (day < earliestAvailabilityDay(now, type) || day > latestAvailabilityDay(now, type))
  ) {
    return [];
  }
  const floor = now.getTime() + MIN_LEAD_MS;
  return all.filter((hour) => new Date(isoFromLocalSlot(day, hour)).getTime() >= floor);
}

export function earliestAvailabilityDay(now: Date = new Date(), type: AppointmentType = 'interview'): string {
  if (type === 'medical_exam') return addMxBusinessDays(portalToday(now), EXAM_MIN_BUSINESS_DAYS);
  return dayAfter(now, MIN_LEAD_DAYS);
}

export function latestAvailabilityDay(now: Date = new Date(), type: AppointmentType = 'interview'): string {
  if (type === 'medical_exam') return addCalendarDays(portalToday(now), MAX_LEAD_DAYS);
  return dayAfter(now, MAX_LEAD_DAYS);
}

/**
 * «Hoy» en hora de Ciudad de México (`YYYY-MM-DD`), no en la del dispositivo:
 * a las 23:00 de México ya es mañana en UTC, y contar desde ahí corría el
 * mínimo del examen un día hábil.
 */
export function portalToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: PORTAL_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/**
 * Día + hora elegidos → instante ISO, interpretando en hora de México.
 *
 * NO se usa la zona del navegador. El portal muestra todas sus fechas en hora
 * de Ciudad de México (`PORTAL_TIME_ZONE`) y el modal lo dice; si el selector
 * interpretara en la zona del dispositivo, un cliente de viaje elegiría «10:00»
 * y la fila le confirmaría otra hora. El producto habla una sola hora.
 *
 * La conversión no usa librería: se arma el instante como si los componentes
 * fueran UTC, se mira qué hora marca ESE instante en México, y la diferencia
 * es el offset a descontar. Una pasada alcanza porque México no mueve el reloj
 * desde 2022; y aunque lo moviera, el salto solo erraría en la hora exacta del
 * cambio.
 */
export function isoFromLocalSlot(day: string, hour: string): string {
  const [year, month, date] = day.split('-').map(Number);
  const [hours, minutes] = hour.split(':').map(Number);
  const asIfUtc = Date.UTC(year, (month ?? 1) - 1, date ?? 1, hours ?? 0, minutes ?? 0, 0, 0);
  return new Date(asIfUtc - zoneOffsetMs(asIfUtc)).toISOString();
}

/** Cuánto adelanta/atrasa Ciudad de México respecto de UTC en ese instante. */
function zoneOffsetMs(instant: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: PORTAL_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(instant));

  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  // `hour12: false` puede devolver 24 por la medianoche: Date.UTC lo normaliza.
  const asZone = Date.UTC(
    read('year'), read('month') - 1, read('day'),
    read('hour'), read('minute'), read('second'),
  );
  return asZone - instant;
}

/** La zona en la que el portal muestra y lee todas sus fechas. */
const PORTAL_TIME_ZONE = 'America/Mexico_City';

function dayAfter(now: Date, days: number): string {
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * El inverso de `isoFromLocalSlot`: instante ISO → el día y la hora que el
 * selector tiene que mostrar, en hora de México.
 *
 * Tiene que ser el inverso exacto. Leer con la zona del dispositivo y escribir
 * con la de México hacía que el cliente guardara las 10:00 y al reabrir viera
 * las 13:00, y que una hora válida cayera fuera de la grilla y el selector
 * apareciera vacío.
 */
export function localSlotFromIso(iso: string): { day: string; hour: string } {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return { day: '', hour: '' };

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: PORTAL_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const read = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  // `hour12: false` devuelve «24» para la medianoche en algunos motores.
  const hour = read('hour') === '24' ? '00' : read('hour');
  return {
    day: `${read('year')}-${read('month')}-${read('day')}`,
    hour: `${hour}:${read('minute')}`,
  };
}
